/**
 * Plan-change confirmation dialog - shows the Stripe-computed "due today EUR X, then EUR Y
 * from <date>" plus the invoice-line breakdown, and resolves a shrinking site quota on the
 * spot (buy "+1 site" OR choose which sites stay live).
 *
 * The quota choice is CONTROLLED from outside (`resolution` + `onResolutionChange`):
 * changing it means a fresh preview from Stripe (the add-on really does change the amount),
 * and that fetch is driven by the parent (`PlanComparison`), which owns `onPreview`.
 *
 * Moved out of `PlanComparison.tsx` into its own folder: that file was already 730+ lines
 * and the quota resolution added its own state plus two subcomponents (AGENTS.md §4).
 */
import { Loader2 } from "lucide-react";
import { Button } from "../../components/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/dialog";
import { formatDate, formatMoney, makeT, planChangeLineLabel } from "../i18n";
import type { Locale, PlanChangePreview } from "../types";
import { ImpactList } from "./ImpactList";
import { QuotaResolution } from "./QuotaResolution";
import { hasImpacts, keepIsComplete, needsQuotaResolution, type QuotaResolution as Resolution } from "./lib";

export interface PlanChangeDialogProps {
  open: boolean;
  kind: "upgrade" | "downgrade";
  state: "loading" | "ready" | "error";
  errorKind: "calc" | "config";
  preview: PlanChangePreview | null;
  applying: boolean;
  locale: Locale;
  resolution: Resolution;
  onResolutionChange: (next: Resolution) => void;
  /** Whether "choose the surviving sites" is allowed (account owner + caller has the keep API). */
  allowKeep: boolean;
  onConfirm: () => void;
  onContinue: () => void;
  onClose: () => void;
}

export function PlanChangeDialog({
  open,
  kind,
  state,
  errorKind,
  preview,
  applying,
  locale,
  resolution,
  onResolutionChange,
  allowKeep,
  onConfirm,
  onContinue,
  onClose,
}: PlanChangeDialogProps) {
  const t = makeT(locale);
  const noChange = state === "ready" && preview?.change === "none";
  const quota = preview?.quotaOptions;
  const showQuota = state === "ready" && needsQuotaResolution(quota);
  // The picker path needs a finished selection, otherwise the server can't know what to suspend.
  const blocked = showQuota && !keepIsComplete(resolution, quota);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t(`confirm.title.${kind}`)}</DialogTitle>
        </DialogHeader>

        {state === "loading" && (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> {t("confirm.loading")}
          </div>
        )}

        {state === "error" && errorKind === "config" && (
          <>
            <DialogDescription className="py-2">{t("confirm.errorConfig")}</DialogDescription>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>{t("confirm.close")}</Button>
            </DialogFooter>
          </>
        )}

        {state === "error" && errorKind !== "config" && (
          <>
            <DialogDescription className="py-2">{t("confirm.error")}</DialogDescription>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>{t("confirm.cancel")}</Button>
              <Button variant="primary" onClick={onContinue}>{t("confirm.continue")}</Button>
            </DialogFooter>
          </>
        )}

        {state === "ready" && preview && noChange && (
          <>
            <DialogDescription className="py-2">{t("confirm.noChange")}</DialogDescription>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>{t("confirm.close")}</Button>
            </DialogFooter>
          </>
        )}

        {state === "ready" && preview && !noChange && (
          <>
            <div className="max-h-[65vh] space-y-3 overflow-y-auto py-1 pr-0.5">
              {/* Due today (proration) */}
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-muted-foreground">{t("confirm.dueToday")}</span>
                <span className="text-xl font-bold tracking-tight tabular-nums">
                  {formatMoney(preview.amountCents, preview.currency, locale)}
                </span>
              </div>

              {/* Then EUR Y per cycle from <date> */}
              <p className="text-sm text-muted-foreground">
                {t("confirm.then")}{" "}
                <span className="font-medium text-foreground">
                  {formatMoney(preview.recurringCents, preview.currency, locale)}
                  {preview.billingCycle === "yearly" ? t("confirm.perYearSuffix") : t("confirm.perMonthSuffix")}
                </span>
                {preview.nextRenewalAt ? ` · ${t("confirm.from")} ${formatDate(preview.nextRenewalAt, locale)}` : ""}
              </p>

              {preview.change === "downgrade" && (
                <p className="text-xs text-muted-foreground">
                  {t("confirm.downgradeCredit")}
                  {/* On a yearly cycle the unused remainder is large and is NOT a payout - say so. */}
                  {preview.billingCycle === "yearly" && <> {t("confirm.downgradeCreditYearly")}</>}
                </p>
              )}

              {/* Quota resolution: buy extra sites OR choose which ones stay live. */}
              {showQuota && (
                <QuotaResolution
                  quota={quota}
                  resolution={resolution}
                  onChange={onResolutionChange}
                  billingCycle={preview.billingCycle}
                  locale={locale}
                  t={t}
                  disabled={applying}
                  allowKeep={allowKeep}
                />
              )}

              {/* What else is lost (limits below usage + features switching off).
                  `sites.max` is omitted - the quota block already covers it. */}
              {hasImpacts(preview.impacts) && preview.impacts && (
                <ImpactList impacts={preview.impacts} locale={locale} t={t} omitKeys={["sites.max"]} />
              )}

              {/* Stripe's invoice-line breakdown (the truth, exactly as computed) */}
              {preview.lines.length > 0 && (
                <div className="mt-1 rounded-lg border border-border bg-muted/30 p-3">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
                    {t("confirm.breakdown")}
                  </p>
                  <ul className="space-y-1.5">
                    {preview.lines.map((l, i) => (
                      <li key={i} className="flex items-start justify-between gap-3 text-sm">
                        <span className="min-w-0 text-muted-foreground">{planChangeLineLabel(l, locale)}</span>
                        <span className="shrink-0 font-medium tabular-nums">
                          {formatMoney(l.amountCents, preview.currency, locale)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Nothing is billed during a trial - explain why the amount is zero. */}
              {preview.lines.length === 0 && preview.amountCents === 0 && (
                <p className="text-xs text-muted-foreground">{t("confirm.trialNoCharge")}</p>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={applying}>{t("confirm.cancel")}</Button>
              <Button variant="primary" onClick={onConfirm} loading={applying} disabled={blocked}>
                {t("confirm.confirm")}
              </Button>
            </DialogFooter>
            {blocked && <p className="text-right text-xs text-amber-700 dark:text-amber-400">{t("quota.pickRequired")}</p>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
